const PINNED_CURRENCY_CODES = [
  "USD",
  "EUR",
  "GBP",
  "TND",
  "CAD",
  "CHF",
  "JPY",
  "AUD",
  "MAD",
  "AED",
  "SAR",
  "BRL",
  "INR",
  "CNY",
  "MXN",
];

/** ISO 4217 fallback when Intl.supportedValuesOf is unavailable. */
const FALLBACK_CURRENCY_CODES = [
  "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN",
  "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL",
  "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLP", "CNY",
  "COP", "CRC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP",
  "ERN", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GHS", "GIP", "GMD",
  "GNF", "GTQ", "GYD", "HKD", "HNL", "HTG", "HUF", "IDR", "ILS", "INR",
  "IQD", "IRR", "ISK", "JMD", "JOD", "JPY", "KES", "KGS", "KHR", "KMF",
  "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LKR", "LRD", "LSL", "LYD",
  "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU", "MUR", "MVR",
  "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK", "NPR", "NZD",
  "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RON",
  "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK", "SGD", "SHP",
  "SLE", "SOS", "SRD", "SSP", "STN", "SYP", "SZL", "THB", "TJS", "TMT",
  "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "UYU",
  "UZS", "VES", "VND", "VUV", "WST", "XAF", "XCD", "XOF", "XPF", "YER",
  "ZAR", "ZMW", "ZWG",
];

export function getRestaurantCurrencyCode() {
  return localStorage.getItem("restaurantCurrency") || "USD";
}

export function setRestaurantCurrencyCode(code) {
  const normalizedCode = (code || "USD").toUpperCase();
  localStorage.setItem("restaurantCurrency", normalizedCode);
  return normalizedCode;
}

export function getRestaurantSecondaryCurrencyCode() {
  return String(localStorage.getItem("restaurantSecondaryCurrency") || "")
    .trim()
    .toUpperCase();
}

export function setRestaurantSecondaryCurrencyCode(code) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (!normalizedCode) {
    localStorage.removeItem("restaurantSecondaryCurrency");
    return "";
  }
  localStorage.setItem("restaurantSecondaryCurrency", normalizedCode);
  return normalizedCode;
}

export function parseOptionalPrice(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** ISO 3166-1 alpha-2 (or EU) for flag images. Empty when there is no country flag. */
const CURRENCY_FLAG_OVERRIDES = {
  EUR: "EU",
  ANG: "CW",
  XAF: "CM",
  XOF: "SN",
  XCD: "AG",
  XPF: "PF",
  XDR: "",
  XXX: "",
};

export function getCurrencyFlagCountry(code) {
  const normalized = String(code || "").trim().toUpperCase();
  if (!normalized) return "";
  if (Object.prototype.hasOwnProperty.call(CURRENCY_FLAG_OVERRIDES, normalized)) {
    return CURRENCY_FLAG_OVERRIDES[normalized];
  }
  if (normalized.startsWith("X")) return "";
  return normalized.slice(0, 2);
}

export function getCurrencyFlagEmoji(code) {
  const iso = getCurrencyFlagCountry(code);
  if (iso.length !== 2) return "";
  return String.fromCodePoint(
    ...[...iso].map((char) => 127397 + char.charCodeAt(0))
  );
}

export function getCurrencyFlagUrl(code, width = 40) {
  const iso = getCurrencyFlagCountry(code).toLowerCase();
  if (!iso) return "";
  return `https://flagcdn.com/w${width}/${iso}.png`;
}

export function getCurrencySymbol(code) {
  const normalized = (code || "USD").toUpperCase();
  if (normalized === "TND") return "DT";
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalized,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(1);
    return parts.find((part) => part.type === "currency")?.value || normalized;
  } catch {
    return normalized;
  }
}

export function getWorldCurrencies(locale = "en") {
  let codes = [];
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      codes = Intl.supportedValuesOf("currency");
    }
  } catch {
    codes = [];
  }
  if (!Array.isArray(codes) || codes.length === 0) {
    codes = FALLBACK_CURRENCY_CODES;
  }

  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "currency" });
  } catch {
    displayNames = null;
  }

  const pinIndex = new Map(PINNED_CURRENCY_CODES.map((code, index) => [code, index]));

  return [...new Set(codes.map((code) => String(code).toUpperCase()))]
    .map((code) => {
      let name = code;
      try {
        name = displayNames?.of(code) || code;
      } catch {
        name = code;
      }
      const symbol = getCurrencySymbol(code);
      const flagCountry = getCurrencyFlagCountry(code);
      const flagEmoji = getCurrencyFlagEmoji(code);
      return {
        code,
        name,
        symbol,
        flagCountry,
        flagEmoji,
        search: `${code} ${name} ${symbol} ${flagCountry}`.toLowerCase(),
      };
    })
    .sort((a, b) => {
      const pinA = pinIndex.has(a.code) ? pinIndex.get(a.code) : 999;
      const pinB = pinIndex.has(b.code) ? pinIndex.get(b.code) : 999;
      if (pinA !== pinB) return pinA - pinB;
      return a.name.localeCompare(b.name, locale);
    });
}

export function formatMoney(amount, currencyCode) {
  const code = (currencyCode || getRestaurantCurrencyCode() || "USD").toUpperCase();
  const value = Number(amount ?? 0);

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value} ${getCurrencySymbol(code)}`;
  }
}

export function formatDualMoney(amount, secondaryAmount, secondaryCode) {
  const main = formatMoney(amount);
  const code = secondaryCode || getRestaurantSecondaryCurrencyCode();
  if (!code || secondaryAmount === "" || secondaryAmount == null) return main;
  return `${main} / ${formatMoney(secondaryAmount, code)}`;
}

const EXCHANGE_RATE_TTL_MS = 5 * 60 * 1000;
const exchangeRateCache = new Map();

function roundConvertedAmount(amount, rate) {
  const value = Number(amount) * Number(rate);
  if (!Number.isFinite(value)) return "";
  return (Math.round(value * 100) / 100).toFixed(2);
}

async function readJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

function rateFromCurrencyApi(payload, from, to) {
  const table = payload?.[from];
  const rate = table?.[to];
  return Number.isFinite(Number(rate)) ? Number(rate) : null;
}

function rateFromOpenErApi(payload, to) {
  const rate = payload?.rates?.[to.toUpperCase()];
  return Number.isFinite(Number(rate)) ? Number(rate) : null;
}

export async function fetchExchangeRate(fromCode, toCode) {
  const from = String(fromCode || "").trim().toLowerCase();
  const to = String(toCode || "").trim().toLowerCase();
  if (!from || !to) throw new Error("Missing currency");
  if (from === to) return { rate: 1, date: new Date().toISOString().slice(0, 10) };

  const cacheKey = `${from}:${to}`;
  const cached = exchangeRateCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < EXCHANGE_RATE_TTL_MS) {
    return cached;
  }

  const sources = [
    {
      url: `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${from}.min.json`,
      parse: (data) => rateFromCurrencyApi(data, from, to),
    },
    {
      url: `https://latest.currency-api.pages.dev/v1/currencies/${from}.min.json`,
      parse: (data) => rateFromCurrencyApi(data, from, to),
    },
    {
      url: `https://open.er-api.com/v6/latest/${from.toUpperCase()}`,
      parse: (data) => rateFromOpenErApi(data, to),
    },
  ];

  let lastError = null;
  for (const source of sources) {
    try {
      const data = await readJson(source.url);
      const rate = source.parse(data);
      if (!rate || rate <= 0) continue;
      const result = {
        rate,
        date: data?.date || data?.time_last_update_utc || "",
        fetchedAt: Date.now(),
      };
      exchangeRateCache.set(cacheKey, result);
      return result;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Exchange rate unavailable");
}

export function convertAmountWithRate(amount, rate) {
  return roundConvertedAmount(amount, rate);
}


